/**
 * ITER308-D · Tenant Brand Theming — colour derivation helpers.
 *
 * Design decision (fork after guest-leak audit): brand CSS variables are
 * NEVER written to `document.documentElement`. That leaks tenant colour
 * onto guest surfaces (loyalty wallet, gift voucher pages, share PNGs)
 * that intentionally use the studio-designed navy + gold palette.
 *
 * Instead:
 *   • `deriveBrandVars(hex)` returns a plain object of CSS variable
 *     entries (`--color-primary`, `-hover`, `-active`, `-tint`, `-tint-2`).
 *   • `Layout.js` (the admin/manager shell) reads it from `AuthContext`
 *     and applies it as an `inline style` on its own root wrapper.
 * Guest surfaces don't render inside `Layout`, so they resolve
 * `var(--color-primary)` from `:root` in `index.css` — the platform green.
 */
function _clamp(n) { return Math.max(0, Math.min(255, n)); }

function _hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return null;
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function _rgbToHex({ r, g, b }) {
  const h = (v) => _clamp(Math.round(v)).toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

// Luminance shift — positive lightens, negative darkens. Preserves hue
// better than a naive HSL twist.
function _shift(hex, pct) {
  const rgb = _hexToRgb(hex);
  if (!rgb) return hex;
  const p = pct / 100;
  const mix = p > 0
    ? { r: rgb.r + (255 - rgb.r) * p,
        g: rgb.g + (255 - rgb.g) * p,
        b: rgb.b + (255 - rgb.b) * p }
    : { r: rgb.r * (1 + p),
        g: rgb.g * (1 + p),
        b: rgb.b * (1 + p) };
  return _rgbToHex(mix);
}

// `hex` at `alpha` opacity rendered against white — matches the visual
// weight of the original opaque tint variables in `index.css`.
function _tintOnWhite(hex, alpha) {
  const rgb = _hexToRgb(hex);
  if (!rgb) return hex;
  return _rgbToHex({
    r: rgb.r * alpha + 255 * (1 - alpha),
    g: rgb.g * alpha + 255 * (1 - alpha),
    b: rgb.b * alpha + 255 * (1 - alpha),
  });
}

/** Return null when `hex` is falsy/invalid — Layout falls back to :root. */
export function deriveBrandVars(hex) {
  if (!hex || !_hexToRgb(hex)) return null;
  return {
    '--color-primary':        hex,
    '--color-primary-hover':  _shift(hex, 9),
    '--color-primary-active': _shift(hex, -6),
    '--color-primary-tint':   _tintOnWhite(hex, 0.08),
    '--color-primary-tint-2': _tintOnWhite(hex, 0.04),
  };
}
